import { useContext } from 'react';
import MessageInputShell from '../FarChat/MessageInputShell';
import { WORKFLOW_QUERY_PLACEHOLDER } from '../HomeChat/DocumentAssistantConversation';
import { WorkflowConversationAction, WorkflowConversationPresentation } from '../HomeChat/WorkflowConversationPresentation';
import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import { TextField } from '../FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';

import Typography from '@mui/material/Typography';
import { apiCall } from '../../api/apiClient';

import type {
  GpcChatField,
  GpcChatMessage,
  GpcChatPendingInput,
  GpcChatRequiredField,
  GpcChatRequiredFieldsValidation,
  GpcChatSessionView,
  GpcSourceObligations,
  GpcChatWorkflow,
  GpcChatWorkflowHandoff,
  GpcChatWorkflowReview,
  GpcChatWorkflowStageKey,
  GpcChatTurnInput,
} from './types';
import { safeDisplayText, safeFormValue } from './display';
import useTurnProgress from '../FarChat/useTurnProgress';
import HomeChatWorkflowBanner from '../HomeChat/HomeChatWorkflowBanner';
import { isGenericStageFormCopy, stageFormGuidance, withoutActiveStagePrompt } from '../HomeChat/stageFormGuidance';
import { sortByUnitPrice } from '../../utils/productPriceOrdering';
import ProductProgramEvidence from '../../stages/ProductProgramEvidence';

function makeRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `gpc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function plainDisplayText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function isGeneratedMfrTranscript(text: string): boolean {
  return /(?:^|\n)\s*(salient characteristics|mfr text|market[- ]research findings)\s*:/i.test(text);
}

function assistantText(view: GpcChatSessionView): string | null {
  const text = plainDisplayText(view.assistant_message ?? view.assistant?.content);
  return text && !isGeneratedMfrTranscript(text) ? text : null;
}

type FieldValue = string | number | boolean;

type SelectOption = { value: string; label: string };

function submittedFormInput(
  fields: GpcChatField[],
  values: Record<string, FieldValue>,
): Record<string, FieldValue> {
  return Object.fromEntries(
    fields.flatMap((field) => {
      if (field.input_type === 'boolean') return [[field.key, values[field.key] === true]];
      const value = values[field.key];
      if (value === undefined) return [];
      // DD-2579 date fields are stored in the ordinary form model as compact
      // YYYYMMDD values. Keep the control native/accessible while preserving
      // that existing payload contract; do not introduce a chat wrapper.
      const submitted = (field.input_type === 'date_yyyymmdd' || field.value_format === 'yyyymmdd') && typeof value === 'string'
        ? value.replaceAll('-', '')
        : value;
      return [[field.key, submitted]];
    })
  );
}

function submittedFormText(fields: GpcChatField[], input: Record<string, FieldValue>): string {
  return fields
    .filter((field) => Object.prototype.hasOwnProperty.call(input, field.key))
    .map((field) => {
      const value = input[field.key];
      const readableValue = field.input_type === 'boolean'
        ? value === true ? 'Yes' : 'No'
        : String(value);
      return `${safeDisplayText(field.label, field.key)}: ${readableValue}`;
    })
    .join('\n');
}

function selectOptions(field: GpcChatField): SelectOption[] {
  return (field.options || []).flatMap((option) => {
    if (typeof option === 'string') {
      return [{ value: option, label: safeDisplayText(option) }];
    }
    if (option && typeof option === 'object' && typeof option.value === 'string') {
      return [{ value: option.value, label: safeDisplayText(option.label, option.value) }];
    }
    return [];
  });
}

function isVisibleField(field: GpcChatField): boolean {
  return field.visible !== false && field.hidden !== true;
}

const GPC_CHAT_FIELD_TYPES: ReadonlySet<GpcChatField['input_type']> = new Set<GpcChatField['input_type']>([
  'text', 'textarea', 'number', 'date', 'date_yyyymmdd', 'email', 'url', 'select', 'radio', 'boolean', 'boolean_choice', 'json',
]);

function asFieldType(value: unknown): GpcChatField['input_type'] {
  return typeof value === 'string' && GPC_CHAT_FIELD_TYPES.has(value as GpcChatField['input_type'])
    ? value as GpcChatField['input_type']
    : 'text';
}

function ruleField(
  rule: GpcChatRequiredField,
  key: unknown,
  fallbackLabel?: unknown,
): GpcChatField | null {
  if (typeof key !== 'string' || !key.trim()) return null;
  return {
    key,
    label: rule.label ?? fallbackLabel ?? key,
    input_type: asFieldType(rule.input_type),
    value_format: rule.value_format === 'yyyymmdd' ? 'yyyymmdd' : undefined,
    required: rule.required !== false,
    help_text: rule.help_text ?? rule.reason,
    options: rule.options,
    candidate_cards: rule.candidate_cards,
    candidates: rule.candidates,
  };
}

/**
 * A non-array unresolved_fields is an older controller response, for which the
 * generic fields remain backward-compatible. An array, including an empty one,
 * is authoritative for blocking fields: never append stale fields that the
 * server did not mark as unresolved and applicable. A small, explicitly
 * server-provided `prefilled_fields` set is the exception: it represents
 * values that are ready to review/edit (currently DD-2579 Block 6), rather
 * than fields that must be collected.
 */
function formFieldsForPending(pending: GpcChatPendingInput | null | undefined): GpcChatField[] {
  if (!pending) return [];
  const fields: GpcChatField[] = [];
  const seenKeys = new Set<string>();
  const add = (field: GpcChatField | null) => {
    if (field && !seenKeys.has(field.key)) {
      seenKeys.add(field.key);
      fields.push(field);
    }
  };
  // These are intentionally displayed before unresolved fields so a
  // cardholder can review the account/activity values in their natural Block
  // 6 order, then provide only the remaining information.
  for (const field of pending.prefilled_fields || []) add(isVisibleField(field) ? field : null);
  if (!Array.isArray(pending.unresolved_fields)) {
    for (const field of pending.fields) add(isVisibleField(field) ? field : null);
    return fields;
  }
  for (const rule of pending.unresolved_fields) {
    const key = rule.path ?? rule.key;
    const controllerField = typeof key === 'string'
      ? pending.fields.find((field) => field.key === key)
      : undefined;
    add(ruleField({
      ...rule,
      input_type: controllerField?.input_type ?? rule.input_type,
      options: controllerField?.options ?? rule.options,
      candidate_cards: controllerField?.candidate_cards ?? rule.candidate_cards,
      candidates: controllerField?.candidates ?? rule.candidates,
    }, key));
    if (rule.evidence) {
      add({
        key: rule.evidence.key,
        label: rule.evidence.label,
        input_type: asFieldType(rule.evidence.input_type),
        required: rule.evidence.required !== false,
        help_text: rule.evidence.help_text,
        options: rule.evidence.options,
      });
    }
  }
  return fields;
}

function isMissingValue(value: FieldValue | undefined): boolean {
  return value === undefined || (typeof value === 'string' && value.trim() === '');
}

function missingFieldMessage(field: GpcChatField): string {
  const label = safeDisplayText(field.label, field.key);
  return field.input_type === 'boolean' || field.input_type === 'boolean_choice'
    ? `Please answer “${label}”.`
    : `Please complete “${label}”.`;
}

function fieldHelperText(fieldError: string | undefined, helpText: unknown): React.ReactNode {
  const help = safeDisplayText(helpText);
  if (fieldError && help) return <>{fieldError}<br />{help}</>;
  return fieldError || help || undefined;
}

function finitePositiveNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function pendingProgressFallback(pending: GpcChatPendingInput | null, startingController: boolean): string {
  if (startingController) return 'Reading your purchase requirement';
  const key = typeof pending?.stage_key === 'string' ? pending.stage_key : '';
  // These are intentionally the same user-facing operations as the backend
  // GPC progress catalog. A poll can legitimately arrive just after a turn
  // begins, so this snapshot-race fallback must still describe the concrete
  // cache/agent work rather than a generic "Saving" state.
  if (key === 'naics_psc') return 'Finding relevant NAICS and PSC codes';
  if (key === 'mandatory_source') return 'Preparing mandatory-source options';
  if (key === 'quotes') return 'Preparing competition quotes from source results';
  if (key === '889') return 'Preparing the Section 889 representation';
  if (key === 'abstract') return 'Drafting the GPC Abstract';
  if (key === 'dd2579') return 'Filling the DD-2579';
  if (key === 'af_form9') return 'Preparing AF Form 9';
  if (key === 'ecars') return 'Preparing eCARS approval information';
  if (key === 'tax_exempt') return 'Preparing tax-exempt documentation';
  if (key === 'receipt') return 'Preparing receipt and acceptance information';
  if (key === 'iuid') return 'Preparing IUID and property tracking information';
  const keys = new Set((pending?.fields || []).map((field) => field.key));
  if (keys.has('mfr_text')) return 'Identifying salient characteristics';
  if (keys.has('unit_price') || keys.has('quantity') || keys.has('product_name') || keys.has('product')) return 'Saving product and purchase details';
  return 'Checking required workflow information';
}

function progressLabel(
  steps: Array<{ step_id?: string; label: string; state: string }>,
  fallback: string,
  currentStepId: string | null,
): string {
  // `current_step_id` is atomically updated with the step list by the
  // backend. Trust it first, but only when it still points at a running step;
  // a terminal/stale id must never become a spinner label.
  const current = currentStepId
    ? steps.find((step) => step.step_id === currentStepId && step.state === 'running' && step.label.trim())
    : undefined;
  if (current) return current.label;
  const running = [...steps].reverse().find((step) => step.state === 'running' && step.label.trim());
  return running?.label || fallback;
}

function mandatorySourceCaption(reason: unknown, instructions: unknown): string {
  const base = safeDisplayText(reason);
  const guidance = safeDisplayText(instructions)
    // The controller's threshold caption already says why this source review
    // is required. Remove that repeated lead-in before folding the useful
    // selection guidance into the one caption the cardholder reads.
    .replace(
      /^Because this purchase is above the applicable micro-purchase threshold,\s*review the required-source listings before selecting a vendor\.\s*/i,
      '',
    )
    .trim();
  return [base, guidance].filter(Boolean).join(' ');
}

function dateControlValue(field: GpcChatField, value: FieldValue | undefined): string | number | boolean {
  if ((field.input_type !== 'date_yyyymmdd' && field.value_format !== 'yyyymmdd') || typeof value !== 'string') return value ?? '';
  // A persisted DD-2579 compact date must be converted before assigning it to
  // an HTML date input, whose value grammar is always YYYY-MM-DD.
  return /^\d{8}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}` : value;
}

type RadioCandidate = { value: string; label: string; source: string; vendor: string; product: string; price: string; total: string; detail: string; detailUrl: string | null; reference: string };

/** A compact, read-only projection of the canonical Stage-802 price rows. */
type DiscoveredProduct = {
  id: string;
  source: string;
  vendor: string;
  product: string;
  unitPrice: number | null;
  quantity: number | null;
  totalPrice: number | null;
  detail: string;
  priceStatus: string;
  detailUrl: string | null;
  selectable: boolean;
  /** Server UUIDs are authoritative; legacy selection is price-only. */
  selectionKind: 'authoritative' | 'legacy' | 'none';
};

const LISTING_OWNED_FORM_KEYS = new Set(['product_name', 'unit_price', 'quantity', 'source', 'vendor_name', 'vendor']);

function safeExternalUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function radioCandidates(field: GpcChatField): RadioCandidate[] {
  const raw: Array<Record<string, unknown>> = Array.isArray(field.candidates)
    ? field.candidates
    : Array.isArray(field.candidate_cards)
      ? field.candidate_cards
      : (field.options || []).flatMap((option) => {
        if (typeof option === 'string') return [{ value: option, label: option }];
        return option && typeof option === 'object' ? [option] : [];
      });
  return raw.flatMap((candidate) => {
    const stable = candidate.value ?? candidate.id ?? candidate.source_id ?? candidate.product_id;
    if (typeof stable !== 'string' || !stable.trim()) return [];
    const text = (key: string) => plainDisplayText(candidate[key]);
    const price = text('price') || text('unit_price');
    const total = text('total') || text('total_price') || text('award_total');
    const detailUrl = safeExternalUrl(candidate.detail_url);
    const reference = text('detail_reference') || text('evidence_summary') || text('reference') || text('evidence_reference');
    const detail = [text('contract'), text('contract_number'), text('evidence'), text('availability'), !detailUrl && reference]
      .filter(Boolean).join(' · ');
    return [{
      value: stable,
      label: text('label'),
      source: text('source') || text('source_name') || text('schedule'),
      vendor: text('vendor') || text('vendor_name') || text('manufacturer'),
      product: text('product') || text('product_name') || text('description'),
      price, total, detail, detailUrl, reference,
    }];
  });
}

function discoveredProducts(pending: GpcChatPendingInput | null): DiscoveredProduct[] {
  const discovery = pending?.discovery;
  const productListings = pending?.product_listings;
  const priceQuotes = discovery?.price_quotes;
  const discoveredProductRecords = discovery?.products;
  const productSelection = pending?.product_selection;
  const selectionCandidates = productSelection?.candidates;
  const isLegacyPriceSelection = productSelection?.field_key === 'selected_unit_price';
  const selectableIds = new Set(
    Array.isArray(selectionCandidates)
      ? selectionCandidates.map((candidate) => plainDisplayText(candidate.listing_id ?? candidate.id)).filter(Boolean)
      : [],
  );
  // `product_listings` is the stable, display-only projection of
  // the ordinary Stage-802 cache. Sessions written before that projection
  // retain the existing `discovery.price_quotes` shape, so it remains a
  // read-only compatibility fallback rather than a second source of truth.
  const records = Array.isArray(selectionCandidates) && selectionCandidates.length > 0
    ? selectionCandidates
    : Array.isArray(productListings)
    ? productListings
    : Array.isArray(priceQuotes) && priceQuotes.length > 0
      ? priceQuotes
      : Array.isArray(discoveredProductRecords) ? discoveredProductRecords : [];
  const normalized = records.map((record, index) => {
    const text = (...keys: string[]) => {
      for (const key of keys) {
        const value = plainDisplayText(record[key]);
        if (value) return value;
      }
      return '';
    };
    return {
      id: text('listing_id', 'id', 'quote_id', 'product_id') || `${index}-${text('vendor_name', 'vendor')}-${text('product_name', 'name', 'product')}`,
      source: text('source', 'source_name', 'schedule'),
      vendor: text('vendor_name', 'vendor', 'manufacturer', 'supplier'),
      product: text('product_name', 'name', 'product', 'description'),
      unitPrice: finitePositiveNumber(record.price ?? record.unit_price ?? record.unitPrice),
      quantity: finitePositiveNumber(record.quantity),
      totalPrice: finitePositiveNumber(record.total_price ?? record.total ?? record.award_total),
      detail: [text('availability'), text('relevant_details', 'rationale', 'spec_matches')].filter(Boolean).join(' · '),
      priceStatus: text('price_status'),
      detailUrl: safeExternalUrl(record.url ?? record.detail_url ?? record.product_url),
      selectable: false,
      selectionKind: 'none' as const,
    };
  });
  const hasAuthoritativeCandidates = Array.isArray(selectionCandidates) && selectionCandidates.length > 0;
  const priceCounts = new Map<number, number>();
  for (const product of normalized) {
    if (product.unitPrice !== null) priceCounts.set(product.unitPrice, (priceCounts.get(product.unitPrice) || 0) + 1);
  }
  const productsWithSelection = normalized.map((product) => {
    if (hasAuthoritativeCandidates && isLegacyPriceSelection) {
      // The backend exposes only uniquely-priced pre-ID quotes here. The
      // price—not a synthesized browser identifier—is its selection key.
      return product.unitPrice !== null
        ? { ...product, selectable: true, selectionKind: 'legacy' as const }
        : product;
    }
    if (hasAuthoritativeCandidates) {
      return selectableIds.has(product.id)
        ? { ...product, selectable: true, selectionKind: 'authoritative' as const }
        : product;
    }
    // Older price search records have no controller-issued listing UUID. A
    // price is the only backend-recognized legacy choice, so make a row
    // selectable only when that price is unique; duplicate-price and
    // contact-for-price rows remain manual rather than inventing an identity.
    if (product.unitPrice !== null && priceCounts.get(product.unitPrice) === 1) {
      return { ...product, selectable: true, selectionKind: 'legacy' as const };
    }
    return product;
  });
  // Sorting is strictly a display projection. `id`, selection kind, and the
  // fields copied from a selected listing remain attached to their own row.
  return sortByUnitPrice(productsWithSelection, (product) => product.unitPrice);
}

function isSelectedDiscoveredProduct(
  product: DiscoveredProduct,
  selectedName: FieldValue | undefined,
  selectedUnitPrice: number | null,
): boolean {
  if (selectedUnitPrice === null || product.unitPrice === null || Math.abs(product.unitPrice - selectedUnitPrice) > 0.005) return false;
  const selected = typeof selectedName === 'string' ? selectedName.trim().toLowerCase() : '';
  // Price is the actual Stage-802 selection basis. When the discovery service
  // also names the item, require that name to match; records without a product
  // name still accurately identify the selected price source.
  return !selected || !product.product || product.product.trim().toLowerCase() === selected;
}

function selectedListingIdForPending(pending: GpcChatPendingInput | null | undefined): string | null {
  const candidate = pending?.product_selection?.selected_listing_id
    ?? pending?.selected_listing_id
    ?? pending?.values?.selected_listing_id
    ?? pending?.defaults?.selected_listing_id
    ?? pending?.candidate_values?.selected_listing_id;
  return typeof candidate === 'string' && candidate.trim() ? candidate : null;
}

function productSelectionFieldKey(pending: GpcChatPendingInput | null | undefined): string {
  const key = pending?.product_selection?.field_key;
  return typeof key === 'string' && key.trim() ? key : 'selected_listing_id';
}

function listingValueUpdates(product: DiscoveredProduct, fields: GpcChatField[]): Record<string, FieldValue> {
  const keys = new Set(fields.map((field) => field.key));
  const updates: Record<string, FieldValue> = {};
  const set = (key: string, value: string | number | null) => {
    if (keys.has(key) && value !== null && value !== '') updates[key] = value;
  };
  set('product_name', product.product);
  set('unit_price', product.unitPrice);
  set('quantity', product.quantity);
  // New controller responses expose these as editable form fields. Retaining
  // the aliases makes saved flows from the existing workflow model work too.
  set('source', product.source);
  set('vendor_name', product.vendor);
  set('vendor', product.vendor);
  return updates;
}

function effectiveSelectedListingId(
  pending: GpcChatPendingInput | null,
  values: Record<string, FieldValue>,
  selectedListingId: string | null,
  listingManuallyEdited = false,
): string | null {
  if (listingManuallyEdited) return null;
  if (selectedListingId) return selectedListingId;
  const selectedPrice = finitePositiveNumber(values.unit_price ?? pending?.derived_total?.unit_price);
  const selected = discoveredProducts(pending).find((product) => (
    product.selectable && isSelectedDiscoveredProduct(product, values.product_name, selectedPrice)
  ));
  return selected?.id ?? null;
}

const LEGACY_COMPLETION_PROMPT = 'All applicable GPC workflow stages have been saved';

function isWorkflowReviewReady(
  workflow: GpcChatWorkflow | null,
  workflowReview: GpcChatWorkflowReview | null,
  requiredFieldsValidation: GpcChatRequiredFieldsValidation | null,
): boolean {
  const validations = [
    workflow?.required_fields_validation,
    workflowReview?.required_fields_validation,
    requiredFieldsValidation,
  ];
  if (validations.some((validation) => validation?.ready === false || validation?.valid === false)) return false;
  if (workflowReview?.ready === true) return true;
  const status = typeof workflow?.status === 'string' ? workflow.status.toLowerCase() : '';
  return status === 'workflow_review_ready' || status === 'review_ready' || status === 'ready_for_review';
}

function workflowFlowId(
  workflow: GpcChatWorkflow | null,
  workflowReview: GpcChatWorkflowReview | null,
): string | null {
  const reviewFlowId = typeof workflowReview?.flow_id === 'string' ? workflowReview.flow_id.trim() : '';
  const workflowId = typeof workflow?.flow_id === 'string' ? workflow.flow_id.trim() : '';
  return reviewFlowId || workflowId || null;
}

const GPC_WORKFLOW_STAGE_KEYS: ReadonlySet<GpcChatWorkflowStageKey> = new Set<GpcChatWorkflowStageKey>([
  'naics_psc', 'mandatory_source', 'quotes', '889', 'abstract', 'dd2579',
  'af_form9', 'ecars', 'tax_exempt', 'receipt', 'iuid',
]);

function asGpcWorkflowStageKey(value: unknown): GpcChatWorkflowStageKey | null {
  return typeof value === 'string' && GPC_WORKFLOW_STAGE_KEYS.has(value as GpcChatWorkflowStageKey)
    ? value as GpcChatWorkflowStageKey
    : null;
}

/**
 * Forward a navigation hint only when the owner-bound controller supplied
 * workflow metadata. Prefer its active/pending form; otherwise use its latest
 * applicable form. The app still validates it against freshly fetched flow
 * metadata before selecting a stage.
 */
function workflowStageHint(workflow: GpcChatWorkflow | null): GpcChatWorkflowStageKey | null {
  const metadata = workflow?.stage_metadata;
  if (!metadata) return null;
  const applicable = Array.isArray(metadata.applicable_stage_keys)
    ? metadata.applicable_stage_keys.map(asGpcWorkflowStageKey).filter((key): key is GpcChatWorkflowStageKey => key !== null)
    : [];
  const current = asGpcWorkflowStageKey(metadata.current_stage_key);
  if (current && applicable.includes(current)) return current;
  return applicable[applicable.length - 1] || null;
}

type WorkflowHandoffAction = {
  label: 'Review workflow' | 'Complete required fields in workflow';
  flowId: string;
  targetStageHint: GpcChatWorkflowStageKey | null;
  /** Required-fields navigation is an interim recovery, not a terminal review. */
  terminal: boolean;
};

function workflowHandoffAction(
  handoff: GpcChatWorkflowHandoff | null | undefined,
  latestStageHint: GpcChatWorkflowStageKey | null,
): WorkflowHandoffAction | null {
  if (!handoff || handoff.available !== true || typeof handoff.flow_id !== 'string' || !handoff.flow_id.trim()) return null;
  // The controller's navigation stage is its latest applicable form. Keep the
  // blocker target as a compatibility fallback for older handoff envelopes.
  const navigationStageHint = asGpcWorkflowStageKey(handoff.navigation_stage_key)
    ?? asGpcWorkflowStageKey(handoff.target_stage_key);
  if (handoff.mode === 'review') {
    return {
      label: 'Review workflow',
      flowId: handoff.flow_id.trim(),
      targetStageHint: navigationStageHint ?? latestStageHint,
      terminal: true,
    };
  }
  if (handoff.mode === 'complete_required_fields') {
    if (!navigationStageHint) return null;
    return {
      label: 'Complete required fields in workflow',
      flowId: handoff.flow_id.trim(),
      targetStageHint: navigationStageHint,
      terminal: false,
    };
  }
  return null;
}

/**
 * The controller may return a known default or a candidate extracted from the
 * original Home Chat message. Preserve those values in the editable form so
 * the user can confirm or revise them instead of having to retype them.
 */
function seededFieldValues(pending: GpcChatPendingInput | null | undefined): Record<string, FieldValue> {
  if (!pending) return {};
  const values: Record<string, FieldValue> = {};
  for (const field of formFieldsForPending(pending)) {
    const candidate = pending.values?.[field.key]
      ?? pending.defaults?.[field.key]
      ?? pending.candidate_values?.[field.key]
      ?? field.value
      ?? field.default
      ?? field.candidate
      ?? field.default_value
      ?? field.candidate_value;
    if (field.input_type === 'boolean') {
      // An unchecked box is a concrete No from the first render, never an
      // unanswered tri-state value. Accept older string controller values too.
      values[field.key] = candidate === true || candidate === 'true';
      continue;
    }
    const safeValue = safeFormValue(candidate);
    if (safeValue !== undefined) values[field.key] = safeValue;
  }
  return values;
}

interface Props {
  /** The controller view returned by the confirmed Home Chat router handoff. */
  initialView?: GpcChatSessionView | null;
  /** Sends a bound GPC turn through the Home Chat endpoint. */
  onTurn?: (input: GpcChatTurnInput) => Promise<GpcChatSessionView>;
  /** Temporary opening text shown while Home Chat waits for the first controller view. */
  optimisticOpeningMessage?: string | null;
  /** Selects through the app shell; a stage hint exists only from server metadata. */
  onOpenWorkflow?: (flowId: string, targetStageHint?: GpcChatWorkflowStageKey) => void;
  /**
   * A saved Home Chat session is being rehydrated. Keep the durable GPC
   * transcript visible while the authoritative controller view is refreshed
   * instead of replacing the pane with an empty loading state.
   */
  rehydrating?: boolean;
  /**
   * A persisted Home Chat turn is still running after a reopen. This is
   * server-owned liveness, distinct from a form submission initiated by this
   * mounted panel.
   */
  processingRequestId?: string | null;
  /** Home Chat's owner-scoped cancellation affordance, colocated with busy work. */
  processingAction?: React.ReactNode;
  /** Main Home Chat's launcher-owned utility, never supplied by a companion. */
  headerAction?: React.ReactNode;
}

/**
 * Dedicated transactional GPC chat rendered inside Home Chat after the server
 * binds the conversation. Every turn returns through the Home Chat transport.
 */
const GpcChatPanel: React.FC<Props> = ({
  initialView = null,
  onTurn,
  optimisticOpeningMessage = null,
  onOpenWorkflow,
  rehydrating = false,
  processingRequestId = null,
  processingAction,
  headerAction,
}) => {
  const { minimal } = useContext(WorkflowConversationPresentation);
  const [sessionId, setSessionId] = useState<string | null>(initialView?.session_id || null);
  // Seed the controller view synchronously. In particular, a native outlined
  // select must receive its default before its first browser paint so MUI can
  // render both the shrunken InputLabel and its matching outlined notch.
  const [messages, setMessages] = useState<GpcChatMessage[]>(() => initialView?.messages || []);
  const [pendingInput, setPendingInput] = useState<GpcChatPendingInput | null>(() => initialView?.pending_input || null);
  const [workflow, setWorkflow] = useState<GpcChatWorkflow | null>(() => initialView?.workflow || null);
  const [workflowReview, setWorkflowReview] = useState<GpcChatWorkflowReview | null>(() => initialView?.workflow_review || null);
  const [sourceObligations, setSourceObligations] = useState<GpcSourceObligations | null>(() => initialView?.source_obligations || null);
  const [sourceReview, setSourceReview] = useState<{ itemId: string; sourceId: string; determination: 'official_review' | 'inapplicable' | 'waiver' } | null>(null);
  const [sourceReference, setSourceReference] = useState('');
  const [sourceRationale, setSourceRationale] = useState('');
  const [waiverIssuer, setWaiverIssuer] = useState('');
  const [waiverApprovalDate, setWaiverApprovalDate] = useState('');
  const [sourceReviewBusy, setSourceReviewBusy] = useState(false);
  const [sourceReviewError, setSourceReviewError] = useState<string | null>(null);
  // `undefined` means a legacy controller did not send the handoff contract;
  // `null`/object are structured responses and must not use prose fallback.
  const [workflowHandoff, setWorkflowHandoff] = useState<GpcChatWorkflowHandoff | null | undefined>(
    () => initialView?.workflow_handoff,
  );
  const [requiredFieldsValidation, setRequiredFieldsValidation] = useState<GpcChatRequiredFieldsValidation | null>(
    () => initialView?.required_fields_validation || null,
  );
  const [values, setValues] = useState<Record<string, FieldValue>>(() => seededFieldValues(initialView?.pending_input));
  const [selectedListingId, setSelectedListingId] = useState<string | null>(
    () => selectedListingIdForPending(initialView?.pending_input),
  );
  const [listingManuallyEdited, setListingManuallyEdited] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [busy, setBusy] = useState(false);
  // The request UUID is set before the POST begins, letting this panel poll
  // the same owner-bound Home Chat progress envelope while the controller is
  // doing search, canonical-form, or LLM work.
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);
  // The form becomes a user-visible text bubble while the server performs the
  // next controller transition. It is replaced by the persisted transcript on
  // success and discarded on error so a failed save remains editable.
  const [submittedFormMessage, setSubmittedFormMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const progressRequestId = busyRequestId || processingRequestId;
  const turnProgress = useTurnProgress(progressRequestId, {
    surface: 'home-chat',
    baseUrl: process.env.REACT_APP_API_URL || '',
  });

  const applyView = useCallback((view: GpcChatSessionView, includeAssistant = true) => {
    setSessionId(view.session_id);
    if (view.messages?.length) setMessages(view.messages);
    else if (includeAssistant) {
      const content = assistantText(view);
      if (content) {
        setMessages((previous) => [
          ...previous,
          // This is controller-provided prompt text, deliberately promoted to
          // a visible transcript message. Persisted messages must carry their
          // own explicit visibility marker.
          { role: 'assistant', content, visible_in_chat: true },
        ]);
      }
    }
    setPendingInput(view.pending_input || null);
    setWorkflow(view.workflow || null);
    setWorkflowReview(view.workflow_review || null);
    setSourceObligations(view.source_obligations || null);
    setSourceReview(null);
    setWorkflowHandoff(view.workflow_handoff);
    setRequiredFieldsValidation(view.required_fields_validation || null);
    setValues(seededFieldValues(view.pending_input));
    setSelectedListingId(selectedListingIdForPending(view.pending_input));
    setListingManuallyEdited(false);
    setFieldErrors({});
    setSubmittedFormMessage(null);
  }, []);

  useEffect(() => {
    if (initialView) {
      // A hydrated/reopened server view is authoritative and cannot inherit a
      // transient request indicator from the session it replaced.
      setBusy(false);
      setBusyRequestId(null);
      applyView(initialView);
    }
  }, [initialView, applyView]);

  const visibleFields = useMemo(
    () => {
      const selectionKey = productSelectionFieldKey(pendingInput);
      const fields = formFieldsForPending(pendingInput);
      // Product Discovery renders this authoritative choice as rich listing
      // cards above the form. Do not repeat the server's transport radio among
      // ordinary fields below it.
      return pendingInput?.product_selection ? fields.filter((field) => field.key !== selectionKey) : fields;
    },
    [pendingInput]
  );

  const missingRequiredFields = useMemo(
    () => visibleFields.filter((field) => (
      field.required
      && (field.input_type === 'boolean_choice'
        ? values[field.key] !== true && values[field.key] !== false
        : field.input_type !== 'boolean' && isMissingValue(values[field.key]))
    )),
    [values, visibleFields]
  );

  // Transcript visibility is server-owned. In particular, do not infer an
  // opening purchase statement from a generic `role: user` entry: persisted
  // form submissions use the same role and may contain generated structures.
  const visibleMessages = useMemo(() => {
    const serverMessages = messages.filter((message) => {
      const text = plainDisplayText(message.content);
      if (!text || (message.role === 'assistant' && isGeneratedMfrTranscript(text))) return false;
      return message.role === 'user'
        ? message.is_opening_purchase_statement === true || message.visible_in_chat === true
        : message.visible_in_chat === true;
    });
    const optimisticText = plainDisplayText(optimisticOpeningMessage);
    const serverAlreadyOwnsOpening = serverMessages.some((message) => (
      message.role === 'user'
      && message.is_opening_purchase_statement === true
      && plainDisplayText(message.content) === optimisticText
    ));
    const transcriptMessages = optimisticText && !serverAlreadyOwnsOpening
      ? [{ role: 'user' as const, content: optimisticText, is_opening_purchase_statement: true }, ...serverMessages]
      : serverMessages;
    const visibleHistory = submittedFormMessage
      ? [...transcriptMessages, { role: 'user' as const, content: submittedFormMessage, visible_in_chat: true }]
      : transcriptMessages;
    return withoutActiveStagePrompt(visibleHistory, pendingInput?.prompt);
  }, [messages, optimisticOpeningMessage, submittedFormMessage, pendingInput]);

  const send = useCallback(async (confirmation?: 'confirmed' | 'revise') => {
    if (!sessionId || !onTurn || busy) return;
    const stableSelectedListingId = effectiveSelectedListingId(pendingInput, values, selectedListingId, listingManuallyEdited);
    const selectionCandidates = pendingInput?.product_selection?.candidates;
    const hasSelectionCandidates = Array.isArray(selectionCandidates) && selectionCandidates.length > 0;
    const manualLegacyEntry = pendingInput?.product_selection?.field_key === 'selected_unit_price'
      && listingManuallyEdited;
    if (pendingInput?.product_selection?.required === true && hasSelectionCandidates && !stableSelectedListingId && !manualLegacyEntry) {
      setError('Choose a product listing before continuing.');
      return;
    }
    if (pendingInput && missingRequiredFields.length) {
      setFieldErrors(Object.fromEntries(
        missingRequiredFields.map((field) => [field.key, missingFieldMessage(field)])
      ));
      setError(null);
      return;
    }
    const message = freeText.trim();
    if (!pendingInput && !message) return;
    const selectedListing = stableSelectedListingId
      ? discoveredProducts(pendingInput).find((listing) => listing.id === stableSelectedListingId && listing.selectable)
      : undefined;
    const selectionFieldKey = productSelectionFieldKey(pendingInput);
    const submittedFields = visibleFields.filter((field) => (
      field.key !== selectionFieldKey
      && (!selectedListing || selectedListing.selectionKind === 'none' || !pendingInput?.product_selection || !LISTING_OWNED_FORM_KEYS.has(field.key))
    ));
    const input = pendingInput ? {
      ...submittedFormInput(submittedFields, values),
      // A selected source listing is a stable Stage-802 identity, not a
      // display value reconstructed from a vendor name or price.
      ...(selectedListing?.selectionKind === 'authoritative' ? { [selectionFieldKey]: selectedListing.id } : {}),
      ...(selectedListing?.selectionKind === 'legacy' && pendingInput?.product_selection && selectedListing.unitPrice !== null
        ? { [selectionFieldKey]: selectedListing.unitPrice }
        : {}),
    } : undefined;
    const submittedText = pendingInput ? submittedFormText(submittedFields, input!) : null;
    const clientRequestId = makeRequestId();
    setBusyRequestId(clientRequestId);
    setBusy(true);
    setError(null);
    if (message) setMessages((previous) => [...previous, { role: 'user', content: message, visible_in_chat: true }]);
    if (submittedText) setSubmittedFormMessage(submittedText);
    try {
      applyView(await onTurn({
        client_request_id: clientRequestId,
        ...(message ? { message } : {}),
        ...(input ? { input } : {}),
        ...(confirmation ? { confirmation } : {}),
      }));
      setFreeText('');
    } catch (cause) {
      setSubmittedFormMessage(null);
      setError(cause instanceof Error ? cause.message : 'Unable to save that response.');
    } finally {
      setBusy(false);
      setBusyRequestId(null);
    }
  }, [applyView, busy, freeText, listingManuallyEdited, missingRequiredFields, onTurn, pendingInput, selectedListingId, sessionId, values, visibleFields]);

  const renderField = (field: GpcChatField) => {
    const value = values[field.key];
    const fieldError = fieldErrors[field.key];
    const fieldLabelId = `gpc-chat-field-${field.key.replace(/[^a-zA-Z0-9_-]/g, '-')}-label`;
    if (field.input_type === 'radio') {
      const candidates = radioCandidates(field);
      return (
        <FormControl key={field.key} required={field.required} error={Boolean(fieldError)} component="fieldset">
          <FormLabel component="legend" id={fieldLabelId}>{safeDisplayText(field.label, field.key)}</FormLabel>
          <RadioGroup
            name={field.key}
            aria-labelledby={fieldLabelId}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              setValues((current) => ({ ...current, [field.key]: event.target.value }));
              setFieldErrors((current) => { const { [field.key]: _removed, ...remaining } = current; return remaining; });
            }}
          >
            {candidates.map((candidate) => (
              <Paper key={candidate.value} variant="outlined" sx={{ mt: 1, borderColor: value === candidate.value ? 'primary.main' : 'divider' }}>
                <FormControlLabel
                  value={candidate.value}
                  disabled={busy}
                  control={<Radio />}
                  sx={{ m: 0, alignItems: 'flex-start', width: '100%', p: 1.25 }}
                  label={<Stack spacing={0.25}>
                    <Typography variant="subtitle2">{candidate.label || candidate.source || candidate.vendor || candidate.value}</Typography>
                    {candidate.vendor && <Typography variant="body2">{candidate.vendor}</Typography>}
                    {candidate.product && <Typography variant="body2" color="text.secondary">{candidate.product}</Typography>}
                    {(candidate.price || candidate.total) && <Typography variant="body2">{[candidate.price && `Price: ${candidate.price}`, candidate.total && `Total: ${candidate.total}`].filter(Boolean).join(' · ')}</Typography>}
                    {candidate.detail && <Typography variant="caption" color="text.secondary">{candidate.detail}</Typography>}
                    {candidate.detailUrl && <Typography
                      component="a"
                      href={candidate.detailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      color="primary"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      sx={{ alignSelf: 'flex-start' }}
                    >{candidate.reference || candidate.detailUrl}</Typography>}
                  </Stack>}
                />
              </Paper>
            ))}
          </RadioGroup>
          {(fieldError || safeDisplayText(field.help_text)) && <Typography variant="caption" color={fieldError ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5 }}>{fieldHelperText(fieldError, field.help_text)}</Typography>}
        </FormControl>
      );
    }
    if (field.input_type === 'boolean') {
      return (
        <FormControlLabel
          key={field.key}
          control={<Checkbox
            checked={value === true}
            disabled={busy}
            onChange={(event) => {
              setValues((current) => ({ ...current, [field.key]: event.target.checked }));
              setFieldErrors((current) => {
                const { [field.key]: _removed, ...remaining } = current;
                return remaining;
              });
            }}
          />}
          label={safeDisplayText(field.label, field.key)}
          sx={fieldError ? { color: 'error.main' } : undefined}
        />
      );
    }
    if (field.input_type === 'boolean_choice') {
      return (
        <FormControl key={field.key} required={field.required} error={Boolean(fieldError)} component="fieldset">
          <FormLabel component="legend" id={fieldLabelId}>{safeDisplayText(field.label, field.key)}</FormLabel>
          <RadioGroup
            name={field.key}
            aria-labelledby={fieldLabelId}
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(event) => {
              setValues((current) => ({ ...current, [field.key]: event.target.value === 'true' }));
              setFieldErrors((current) => {
                const { [field.key]: _removed, ...remaining } = current;
                return remaining;
              });
            }}
          >
            <FormControlLabel value="true" disabled={busy} control={<Radio />} label="Yes" />
            <FormControlLabel value="false" disabled={busy} control={<Radio />} label="No" />
          </RadioGroup>
          {(fieldError || safeDisplayText(field.help_text)) && <Typography variant="caption" color={fieldError ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5 }}>{fieldHelperText(fieldError, field.help_text)}</Typography>}
        </FormControl>
      );
    }
    return (
      <TextField
        key={field.key}
        label={safeDisplayText(field.label, field.key)}
        value={dateControlValue(field, value)}
        onChange={(event) => {
          if (LISTING_OWNED_FORM_KEYS.has(field.key)) {
            setSelectedListingId(null);
            setListingManuallyEdited(true);
          }
          setValues((current) => ({
            ...current,
            [field.key]: field.input_type === 'number' && event.target.value !== ''
              ? Number(event.target.value)
              : event.target.value,
          }));
          setFieldErrors((current) => {
            const { [field.key]: _removed, ...remaining } = current;
            return remaining;
          });
        }}
        SelectProps={field.input_type === 'select' ? { native: true } : undefined}
        // Native outlined selects do not reliably infer a default value early
        // enough to notch their label. Force the normal nonempty-label state
        // from the first render without changing the underlying <select>.
        // Browser-native date inputs do not always notify MUI that the label
        // needs to shrink. Force it so every typed control visibly retains its
        // server-provided field label from the first render.
        InputLabelProps={
          field.input_type === 'date' || field.input_type === 'date_yyyymmdd'
            ? { shrink: true }
            : field.input_type === 'select' && !isMissingValue(value) ? { shrink: true } : undefined
        }
        variant="outlined"
        required={field.required}
        disabled={busy}
        error={Boolean(fieldError)}
        helperText={fieldHelperText(fieldError, field.help_text)}
        select={field.input_type === 'select'}
        type={field.input_type === 'number' || field.input_type === 'date' || field.input_type === 'date_yyyymmdd' || field.input_type === 'email' || field.input_type === 'url'
          ? field.input_type === 'date_yyyymmdd' ? 'date' : field.input_type
          : 'text'}
        multiline={field.input_type === 'textarea'}
        minRows={field.input_type === 'textarea' ? 3 : undefined}
        fullWidth
      >
        {field.input_type === 'select' && (
          <>
            <option value="" aria-label="Select an option">Select…</option>
            {selectOptions(field).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </>
        )}
      </TextField>
    );
  };

  if (!sessionId && !optimisticOpeningMessage && !rehydrating && !processingRequestId) {
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto' }} data-testid="gpc-chat-awaiting-handoff">
        <WorkflowConversationAction />
        <HomeChatWorkflowBanner
          title="GPC purchase assistant"
          subtitle="Continue in Home Chat to start or resume a GPC purchase."
          action={headerAction}
        />
      </Box>
    );
  }

  const startingController = !sessionId;
  const hasNormalFields = visibleFields.length > 0;
  const formConfirmation = pendingInput?.confirmation_required ? 'confirmed' : undefined;
  const workflowReviewReady = isWorkflowReviewReady(workflow, workflowReview, requiredFieldsValidation);
  const sourceChecksPending = sourceObligations?.required === true && sourceObligations.status !== 'ready';
  const completedLegacyWorkflow = plainDisplayText(pendingInput?.prompt) === LEGACY_COMPLETION_PROMPT;
  const completedWorkflowId = workflowFlowId(workflow, workflowReview);
  const workflowStatus = plainDisplayText(workflow?.status).toLowerCase();
  // Completion is server-owned workflow state. The prose is retained only as
  // a legacy fallback because an owner can reopen a completed session whose
  // current completion message has changed between controller versions.
  const completedWorkflowStatus = workflowStatus === 'complete' || workflowStatus === 'completed';
  const requiredContentBlocked = [
    workflow?.required_fields_validation,
    workflowReview?.required_fields_validation,
    requiredFieldsValidation,
  ].some((validation) => validation?.ready === false || validation?.valid === false);
  const workflowNavigationHint = workflowStageHint(workflow);
  const structuredHandoffAction = workflowHandoffAction(workflowHandoff, workflowNavigationHint);
  // Transitional support for sessions written before `workflow_handoff` was
  // introduced. New structured responses are authoritative, including an
  // unavailable handoff, so never reinterpret their prose/status client-side.
  const legacyWorkflowActionLabel = workflowHandoff === undefined && completedWorkflowId
    && !hasNormalFields
    && !requiredContentBlocked
    && (workflowReviewReady || completedWorkflowStatus || completedLegacyWorkflow)
    ? workflowReviewReady ? 'Review workflow' : 'Continue'
    : null;
  const proposedWorkflowAction = structuredHandoffAction || (legacyWorkflowActionLabel && completedWorkflowId
    ? {
      label: legacyWorkflowActionLabel,
      flowId: completedWorkflowId,
      targetStageHint: workflowNavigationHint,
      terminal: true,
    }
    : null);
  const workflowAction = sourceChecksPending && proposedWorkflowAction?.terminal ? null : proposedWorkflowAction;

  const saveSourceReview = async () => {
    if (!sourceReview || !completedWorkflowId || sourceReviewBusy) return;
    setSourceReviewBusy(true);
    setSourceReviewError(null);
    try {
      const response = await apiCall<any>(
        `/api/gpc/flows/${completedWorkflowId}/source-obligations/review`, 'POST', {
          item_id: sourceReview.itemId, source_id: sourceReview.sourceId,
          determination: sourceReview.determination,
          official_reference: sourceReference.trim(), rationale: sourceRationale.trim(),
          ...(sourceReview.determination === 'waiver'
            ? { waiver_issuer: waiverIssuer.trim(), approval_date: waiverApprovalDate } : {}),
        }, { retries: 0 },
      );
      if ((response as any).success === false || !Array.isArray((response as any).checks)) {
        setSourceReviewError((response as any).error || 'The official review could not be saved. Check your authority and evidence.');
        return;
      }
      const rows = (response as any).checks as Array<{ item_id: string; source_id: string; status: string }>;
      setSourceObligations((previous) => previous ? {
        ...previous, status: String((response as any).status || 'pending'),
        checks: previous.checks.map((check) => {
          const updated = rows.find((row) => row.item_id === check.item_id && row.source_id === check.source_id);
          return updated ? { ...check, status: updated.status,
            allowed_determinations: updated.status === 'pending' ? check.allowed_determinations : [] } : check;
        }),
      } : previous);
      setSourceReview(null);
      setSourceReference('');
      setSourceRationale('');
      setWaiverIssuer('');
      setWaiverApprovalDate('');
    } catch {
      setSourceReviewError('The official review could not be saved. Check your authority and evidence.');
    } finally {
      setSourceReviewBusy(false);
    }
  };
  // An in-flight controller turn may encompass agent/LLM/discovery work. Keep
  // this distinct from transcript bubbles: it is transient status, not an
  // assistant answer, and it never replaces the user's scrollable history.
  const gpcActionPending = startingController || busy || rehydrating || Boolean(processingRequestId);
  const gpcActionPendingFallback = rehydrating
    ? 'Loading your saved GPC purchase'
    : pendingProgressFallback(pendingInput, startingController);
  const gpcActionPendingLabel = busy || processingRequestId
    ? progressLabel(turnProgress.steps, gpcActionPendingFallback, turnProgress.current_step_id)
    : gpcActionPendingFallback;
  const instructions = stageFormGuidance(safeDisplayText(pendingInput?.instructions)).join('\n');
  const mandatorySourceCaptionText = pendingInput?.stage_key === 'mandatory_source'
    ? mandatorySourceCaption(pendingInput.reason, instructions)
    : '';
  const formCaption = mandatorySourceCaptionText || plainDisplayText(pendingInput?.reason);
  const derivedUnitPrice = finitePositiveNumber(values.unit_price ?? pendingInput?.derived_total?.unit_price);
  const derivedQuantity = finitePositiveNumber(values.quantity ?? pendingInput?.derived_total?.quantity);
  // The controller supplies a server-derived total for the Product Discovery
  // card.  Keep the client calculation reactive while the user changes the
  // editable price or quantity, but do not hide the total just because an
  // older/reopened pending envelope contains only `total_price`.
  const serverDerivedTotal = finitePositiveNumber(pendingInput?.derived_total?.total_price);
  const derivedTotal = derivedUnitPrice !== null && derivedQuantity !== null
    ? derivedUnitPrice * derivedQuantity
    : serverDerivedTotal;
  const derivedTotalLabel = safeDisplayText(pendingInput?.derived_total?.label, 'Total price');
  // A manual/recovery Product Discovery form may predate the server's
  // `derived_total` envelope. It still needs to show the same transparent
  // arithmetic as soon as the cardholder has supplied both operands.
  const hasProductPriceInputs = visibleFields.some((field) => field.key === 'unit_price')
    && visibleFields.some((field) => field.key === 'quantity');
  const productDiscoveryRows = discoveredProducts(pendingInput);
  const hasSelectableProductListings = productDiscoveryRows.some((listing) => listing.selectable);
  const resolvedSelectedListingId = effectiveSelectedListingId(pendingInput, values, selectedListingId, listingManuallyEdited);

  return (
    <Paper square elevation={0} sx={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="gpc-chat-panel">
      <HomeChatWorkflowBanner
        title="GPC purchase assistant"
        subtitle="Answers are saved only after the required confirmation."
        trailing={workflow && <Chip size="small" label={sourceObligations?.required && sourceObligations.status !== 'ready'
          ? 'Source review pending' : safeDisplayText(workflow.status).replaceAll('_', ' ')} />}
        action={headerAction}
      />
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ flexShrink: 0, mx: { xs: 2, sm: 3 }, mt: 1.5 }}>{error}</Alert>}
      <Box
        data-testid="gpc-chat-scroll-region"
        tabIndex={0}
        aria-label="GPC conversation and pending fields"
        aria-busy={gpcActionPending}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', px: { xs: 2, sm: 3 }, py: 1.5, pr: { xs: 2.5, sm: 3.5 } }}
      >
        <Stack spacing={1} aria-live="polite">
          <WorkflowConversationAction />
          {visibleMessages.map((message, index) => (
            <Box
              key={`${message.role}-${index}`}
              data-testid={`gpc-chat-message-${message.role}-${index}`}
              data-message-role={message.role}
              sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '86%', px: 1.5, py: 1, borderRadius: 2, bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover', color: message.role === 'user' ? 'primary.contrastText' : 'text.primary', whiteSpace: 'pre-wrap' }}
            >
              {plainDisplayText(message.content)}
            </Box>
          ))}
          {sourceObligations?.required && <Paper component="section" aria-label="Required source checks" variant="outlined" sx={{ p: 1.5, mt: 1, alignSelf: 'stretch' }}>
            <Typography component="h3" variant="subtitle2">Required source checks</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sourceObligations.status === 'ready'
                ? 'Required source determinations are recorded. Review the workflow before ordering.'
                : 'Prepare the purchase while these checks are pending. An official determination is needed before it is ready to buy.'}
            </Typography>
            {sourceObligations.checks.map((check) => {
              const name = check.source_id === '2git' ? '2GIT' : check.source_id === 'eocf' ? 'Furniture guide (EOCF)' : check.source_id.toUpperCase().replaceAll('_', ' ');
              const status = ({ pending: 'Pending', evidence_submitted: 'Evidence submitted', verified: 'Verified',
                waiver_approved: 'Waiver approved', not_applicable_after_review: 'Not applicable after review' } as Record<string, string>)[check.status] || 'Review needed';
              return <Box key={`${check.item_id}:${check.source_id}`} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{name} — {check.item_id}</Typography>
                <Typography variant="body2">{status}</Typography>
                {check.status === 'pending' && (check.allowed_determinations || []).map((determination) => (
                  <Button key={determination} size="small" disabled={sourceReviewBusy}
                    aria-label={`Record ${determination === 'official_review' ? 'official review' : determination === 'inapplicable' ? 'inapplicability' : 'waiver'} for ${name} for ${check.item_id}`}
                    onClick={() => setSourceReview({ itemId: check.item_id, sourceId: check.source_id, determination })}>
                    {determination === 'official_review' ? 'Record official review' : determination === 'inapplicable' ? 'Record inapplicability' : 'Record waiver'}
                  </Button>
                ))}
              </Box>;
            })}
            {sourceReview && <Box component="form" onSubmit={(event) => { event.preventDefault(); void saveSourceReview(); }} sx={{ display: 'grid', gap: 1, mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Document {sourceReview.sourceId.toUpperCase()} for {sourceReview.itemId}</Typography>
              <TextField label="Official source reference" value={sourceReference} onChange={(event) => setSourceReference(event.target.value)} required fullWidth size="small" />
              <TextField label="Reasoned determination" value={sourceRationale} onChange={(event) => setSourceRationale(event.target.value)} required fullWidth multiline minRows={2} size="small" />
              {sourceReview.determination === 'waiver' && <>
                <TextField label="Waiver issuing authority" value={waiverIssuer} onChange={(event) => setWaiverIssuer(event.target.value)} required fullWidth size="small" />
                <TextField label="Waiver approval date" type="date" InputLabelProps={{ shrink: true }} value={waiverApprovalDate} onChange={(event) => setWaiverApprovalDate(event.target.value)} required fullWidth size="small" />
              </>}
              {sourceReviewError && <Alert severity="error">{sourceReviewError}</Alert>}
              <Stack direction="row" spacing={1}><Button type="submit" variant="contained" disabled={sourceReviewBusy || !sourceReference.trim() || !sourceRationale.trim() || sourceReview.determination === 'waiver' && (!waiverIssuer.trim() || !waiverApprovalDate)}>
                Save source review
              </Button><Button onClick={() => setSourceReview(null)} disabled={sourceReviewBusy}>Cancel</Button></Stack>
            </Box>}
          </Paper>}
          {gpcActionPending && (
            <Stack
              role="status"
              direction="row"
              spacing={1}
              alignItems="center"
              color="text.secondary"
              data-testid="gpc-chat-busy-status"
              sx={{ alignSelf: 'flex-start', px: 1, py: 0.5 }}
            >
              <CircularProgress size={18} aria-label={gpcActionPendingLabel} />
              <Typography variant="body2">{gpcActionPendingLabel}</Typography>
              {processingAction}
            </Stack>
          )}
{!gpcActionPending && workflowAction ? (
  <Box>
    <Button
      variant="contained"
      onClick={() => {
                  if (workflowAction.targetStageHint) onOpenWorkflow?.(workflowAction.flowId, workflowAction.targetStageHint);
                  else onOpenWorkflow?.(workflowAction.flowId);
                }}
      sx={{ alignSelf: 'flex-start' }}
    >
      {workflowAction.terminal ? 'Review and Verify Workflow' : workflowAction.label}
    </Button>
  </Box>
) : !gpcActionPending && pendingInput ? (
            <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); void send(formConfirmation); }} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <ChatBubbleDisclosure key={JSON.stringify([sessionId, pendingInput.stage_key, visibleFields.map(field => field.key)])} label="GPC form" preview="" alwaysCollapsible defaultExpanded title={!isGenericStageFormCopy(plainDisplayText(pendingInput.prompt)) ? <Typography sx={{ fontWeight: 600 }}>{plainDisplayText(pendingInput.prompt)}</Typography> : undefined}>
              {formCaption && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{formCaption}</Typography>}
              {instructions && pendingInput?.stage_key !== 'mandatory_source' && (
                <Alert severity="info" icon={false} sx={{ mt: 1.25, py: 0.5 }} data-testid="gpc-chat-stage-instructions">
                  <Typography variant="body2">{instructions}</Typography>
                </Alert>
              )}
              {pendingInput.stage_key === 'product_discovery' && <ProductProgramEvidence
                results={pendingInput.discovery?.carpet_ii_check ? { items: [{
                  item_id: 'gpc-carpet', checks: { carpet_ii: pendingInput.discovery.carpet_ii_check },
                }] } : null}
                quoteProducts={pendingInput.discovery?.carpet_ii_products}
              />}
              {productDiscoveryRows.length > 0 && (
                <Box
                  sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
                  data-testid="gpc-chat-product-discovery-results"
                >
                  <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2">Products found</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hasSelectableProductListings
                        ? 'Choose the listing you want to use. Its details fill the editable fields below, where you can still revise them.'
                        : 'No priced listing can be selected automatically. Enter the product and pricing details below.'}
                    </Typography>
                  </Box>
                  <Box role="radiogroup" aria-label="Product search results">
                    <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
                      {productDiscoveryRows.map((product) => {
                        const selected = resolvedSelectedListingId === product.id;
                        const listingQuantity = product.quantity ?? derivedQuantity;
                        const total = product.totalPrice ?? (product.unitPrice !== null && listingQuantity !== null
                          ? product.unitPrice * listingQuantity
                          : null);
                        const selectListing = () => {
                          const updates = listingValueUpdates(product, visibleFields);
                          setSelectedListingId(product.id);
                          setListingManuallyEdited(false);
                          setValues((current) => ({ ...current, ...updates }));
                          setFieldErrors((current) => {
                            const remaining = { ...current };
                            for (const key of Object.keys(updates)) delete remaining[key];
                            return remaining;
                          });
                        };
                        return (
                          <Box
                            key={product.id}
                          component="div"
                          onClick={busy || !product.selectable ? undefined : selectListing}
                          sx={{
                            display: 'block', px: 1.5, py: 1.25, cursor: busy ? 'default' : 'pointer',
                            bgcolor: selected ? 'action.selected' : 'transparent',
                            '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                          }}
                          data-testid={`gpc-chat-product-discovery-row-${product.id}`}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="flex-start">
                            <Radio
                              checked={selected}
                              value={product.id}
                              name="gpc-chat-product-listing"
                              disabled={busy || !product.selectable}
                              inputProps={{ 'aria-label': `Use ${product.product || 'this product listing'} from ${product.vendor || product.source || 'the search results'}` }}
                              onClick={(event) => event.stopPropagation()}
                              onChange={selectListing}
                              sx={{ p: 0.25, mt: 0.1 }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                {selected && <Chip size="small" color="primary" label="Selected" />}
                                {product.source && <Chip size="small" variant="outlined" label={product.source.replaceAll('_', ' ')} />}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {product.product || 'Product listing'}
                                </Typography>
                              </Stack>
                              {product.vendor && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Vendor: {product.vendor}</Typography>}
                              <Typography variant="body2" sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                                Unit price: <strong>{product.unitPrice !== null ? formatCurrency(product.unitPrice) : product.priceStatus === 'on_request' ? 'Price on request' : 'Not listed'}</strong>
                                {listingQuantity !== null && product.unitPrice !== null && total !== null && (
                                  <> · Quantity: {listingQuantity} · Total: <strong>{formatCurrency(total)}</strong></>
                                )}
                              </Typography>
                              {product.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{product.detail}</Typography>}
                              {!product.selectable && product.unitPrice !== null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>Enter the purchase details manually to use this price.</Typography>}
                              {!product.selectable && product.priceStatus === 'on_request' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>Published price unavailable; request or enter a quote manually.</Typography>}
                              {product.detailUrl && (
                                <Typography
                                  component="a"
                                  href={product.detailUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  color="primary"
                                  onClick={(event) => event.stopPropagation()}
                                  sx={{ display: 'inline-block', mt: 0.5 }}
                                >
                                  {product.detailUrl}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      );
                      })}
                    </Stack>
                  </Box>
                </Box>
              )}
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>{visibleFields.map(renderField)}</Stack>
              {(pendingInput?.derived_total || hasProductPriceInputs) && derivedTotal !== null && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.25, fontVariantNumeric: 'tabular-nums' }}
                  data-testid="gpc-chat-derived-total"
                >
                  {derivedTotalLabel}: <strong>{formatCurrency(derivedTotal)}</strong>
                  {derivedQuantity !== null && derivedUnitPrice !== null
                    ? ` (${derivedQuantity} × ${formatCurrency(derivedUnitPrice)})`
                    : null}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={busy}>
                  {hasNormalFields ? 'Save and continue' : pendingInput.confirmation_required ? 'Confirm and continue' : 'Continue'}
                </Button>
                {pendingInput.confirmation_required && !hasNormalFields && <Button variant="text" onClick={() => void send('revise')} disabled={busy}>Revise</Button>}
              </Stack>
              </ChatBubbleDisclosure>
            </Box>
          ) : !gpcActionPending && !minimal ? (
            <Stack direction="row" spacing={1}>
              <TextField fullWidth size="small" label="Reply" value={freeText} onChange={(event) => setFreeText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(); } }} />
              <Button variant="contained" onClick={() => void send()} disabled={!freeText.trim()}>Send</Button>
            </Stack>
          ) : null}
        </Stack>
      </Box>
      {minimal && !pendingInput && !gpcActionPending && <MessageInputShell minimal value={freeText} onChange={setFreeText} onSend={() => { void send(); }} onAttachClick={() => {}} placeholder={WORKFLOW_QUERY_PLACEHOLDER} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(); } }} />}
    </Paper>
  );
};

export default GpcChatPanel;
